<template>
  <div id="assetDetailView">
    <section class="assetDetail-title">
      <h1>{{ asset.title || 'No title' }}</h1>
      <p>Published on {{ formatDate(asset.publish_date) || 'Unknown' }} | Last Ingested on {{ formatDate(asset.updated_at) || 'Unknown' }}</p>
    </section>

    <section class="assetDetail-controls">
      <neb-form-control width="300px">
        <neb-date-picker v-model="metricDateRange"
          mode="range"
          prefix="Metric Date Range"
          :defaultValue="[ '-10000', '0' ]"
          highlightDirty
          clearable
          :disableDayFunc="disableDayFunc"
          :defaultOptions="datePickerDefaultOptions"
          @clear="metricDateRange = [ '-10000', '0' ]"
        />
      </neb-form-control>
    </section>

    <section class="assetDetail-views">
        <div class="assetDetail-metricBox">
          <view-metrics :pageUrlId="pageUrlId"
            :brandIdProp="brandId"
            :metricDateRange="metricDateRange"
          />
        </div>
    </section>

    <section class="assetDetail-socialActions">
      <div class="assetDetail-metricBox">
        <social-metrics :pageUrlId="pageUrlId"
          :brandIdProp="brandId"
          :metricDateRange="metricDateRange"
        />
      </div>
    </section>

    <section class="assetDetail-consumption">
      <div class="assetDetail-metricBox">
        <consumption-metrics :pageUrlId="pageUrlId"
          :brandIdProp="brandId"
          :metricDateRange="metricDateRange"
        />
      </div>
    </section>

    <section class="assetDetail-conversions">
      <div class="assetDetail-metricBox">
        <conversion-metrics :pageUrlId="pageUrlId"
          :brandIdProp="brandId"
          :metricDateRange="metricDateRange"
        />
      </div>
    </section>

    <section class="assetDetail-engagement">
      <div class="assetDetail-engagementBox">
        <asset-detail-engagement :brandIdProp="brandId"
          :pageUrlId="pageUrlId"
          :metricDateRange="metricDateRange"
        />
      </div>
    </section>

    <section class="assetDetail-content">
      <div class="assetDetail-contentBox">
        <asset-detail-content :html="asset.html"
          :url="asset.url"
        />
      </div>
    </section>

    <section class="assetDetail-tags">
      <div class="assetDetail-contentBox">
        <asset-detail-tags v-show="!edittingTags"
          :topics="asset.categories"
          :keywords="asset.keywords"
          :pageUrlId="pageUrlId"
          @action:edit="onEditTags"
        />
        <edit-tags v-show="edittingTags"
          :topics="asset.categories"
          :keywords="asset.keywords"
          :loading="editTagsLoading"
          @action:cancel="edittingTags = false"
          @action:save="onEditTagsSave"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { useAssets, useProperties, useAssetProperties } from '@/features'
import { ref, onMounted, provide, computed } from '@vue/composition-api'
import { datePickerDefaultOptions } from '@/options'
import ViewMetrics from '@/components/AssetDetail/ViewMetrics.vue'
import SocialMetrics from '@/components/AssetDetail/SocialMetrics.vue'
import ConsumptionMetrics from '@/components/AssetDetail/ConsumptionMetrics.vue'
import ConversionMetrics from '@/components/AssetDetail/ConversionMetrics.vue'
import AssetDetailContent from '@/components/AssetDetail/Content.vue'
import AssetDetailEngagement from '@/components/AssetDetail/Engagement.vue'
import AssetDetailTags from '@/components/AssetDetail/Tags.vue'
import EditTags from '@/components/AssetDetail/EditTags.vue'
import { useToast } from 'vue-toastification/composition'
import moment from 'moment'
import store from '@/store'

export default {
  name: 'AssetDetail',

  components: {
    ViewMetrics,
    SocialMetrics,
    ConsumptionMetrics,
    ConversionMetrics,
    AssetDetailContent,
    AssetDetailEngagement,
    AssetDetailTags,
    EditTags
  },

  props: {
    pageUrlId: {
      type: String,
      required: true
    }
  },

  setup (props) {
    const properties = useProperties()
    provide('properties', properties)
    const assetProperties = useAssetProperties()
    provide('assetProperties', assetProperties)
    const toast = useToast()

    const assets = useAssets()
    const asset = ref({})
    provide('asset', computed(() => asset.value))
    const metricDateRange = ref([ '-10000', '0' ])
    const edittingTags = ref(false)

    const loadAsset = async () => {
      const data = await assets.getAsset(props.pageUrlId)
      asset.value = data
    }

    const disableDayFunc = (year, month, day) => {
      const d1 = new Date(year, month, day)

      return d1 > new Date()
    }

    const editTagsLoading = ref(false)
    const onEditTags = () => { edittingTags.value = true }
    const onEditTagsSave = async ({ newAssetProperties, newKeywords, newTopics }) => {
      editTagsLoading.value = true
      await assets.setAssetProperties({
        pageUrlId: props.pageUrlId,
        assetProperties: newAssetProperties
      })
      const assetUpdateResp = await assets.updateAsset(props.pageUrlId, {
        keywords: newKeywords,
        topics: newTopics.map(t => t.subject)
      })
      asset.value = assetUpdateResp.data
      await assetProperties.loadList({ brandId: store.state.brand.settings.id, assetId: props.pageUrlId })
      editTagsLoading.value = false
      edittingTags.value = false
      toast.success('Asset tags updated')
    }

    const formatDate = (d) => {
      if (d === null || d === undefined) {
        return null
      }
      return moment(d).format('MM/DD/YYYY')
    }

    onMounted(async () => {
      await loadAsset()
    })

    return {
      asset,
      disableDayFunc,
      metricDateRange,
      datePickerDefaultOptions,
      edittingTags,
      onEditTags,
      editTagsLoading,
      onEditTagsSave,
      formatDate
    }
  }
}
</script>

<style lang="scss">
@import '../../components/Nebula/style.scss';

#assetDetailView {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 5;
  margin: auto;
  column-gap: 20px;
  row-gap: 20px;
  width: 1170px;
  padding-bottom: 40px;

  .assetDetail {
    &-metricBox {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid $cloudy-blue;
      padding: 15px;
    }

    &-engagementBox {
      border: 1px solid $very-light-pink;
      padding: 15px;
    }

    &-contentBox {
      border: 1px solid $light-grey-blue;
      padding: 15px;
    }

    &-title {
      grid-column: 1 / span 4;
      grid-row: 1 / span 1;

      h1 {
        font-family: $defaultFont;
        color: $slate-grey;
        font-size: 20px;
        font-weight: 700;
      }
    }

    &-controls {
      grid-column: 1 / span 4;
      grid-row: 2 / span 1;
    }

    &-views {
      grid-column: 1 /span 1;
      grid-row: 3 / span 1;
    }

    &-socialActions {
      grid-column: 2 / span 1;
      grid-row: 3 / span 1;
    }

    &-consumption {
      grid-column: 3 / span 1;
      grid-row: 3 / span 1;
    }

    &-conversions {
      grid-column: 4 / span 1;
      grid-row: 3 / span 1;
    }

    &-engagement {
      grid-column: 1 / span 4;
      grid-row: 4 / span 1;
    }

    &-content {
      grid-column: 1 / span 2;
      grid-row: 5 / span 1;
    }

    &-tags {
      grid-column: 3 / span 2;
      grid-row: 5 / span 1;
    }
  }
}
</style>
